/* eslint-disable max-params */
export const extractSkuCodes = (voucherId, voucher, isAdvance) => {
  const idStr = String(voucherId);

  // Extract all SKU-like patterns
  const codes = idStr.match(/[A-Za-z]+_[\dA-Za-z]+/g) || [idStr];

  // Check if compound ID
  const isCompound = idStr.includes('-');

  if (isCompound && isAdvance) {
    return [codes[0]]; // only first SKU
  }

  return codes;
};

/**
 * Match a voucher ID against the limits array and return the matched limit entry.
 * @param {Array<object>} limits - array of limit objects from the subscriber limits API
 * @param {string|number} voucherId - the voucher ID to match
 * @param voucher
 * @param isAdvance
 * @returns {object|undefined} the matched limit entry, or undefined if no match
 */
export const matchVoucherLimit = (limits, voucherId, voucher, isAdvance) => {
  if (!limits || !voucherId) return;
  const codes = extractSkuCodes(voucherId, voucher, isAdvance);
  return limits.find(limit => {
    const skuParts = limit.sku?.split(',').map(s => s.trim()) || [];
    return codes.some(code => skuParts.includes(code));
  });
};

/**
 * This helper function calculates how many items in the cart
 * correspond to a given voucher limit group.
 *
 * Parameters:
 * - cart: Array of cart items.
 * - limitGroup: The matched voucher limit group containing SKU codes.
 * - isAdvance: Boolean flag to indicate advance booking logic.
 * @param cart
 * @param limitGroup
 * @param isAdvance
 */
export const getCartUsageForLimit = (cart, limitGroup, isAdvance) => {
  if (!limitGroup?.sku || !cart?.length) return 0;

  const skuCodes = new Set(limitGroup.sku.split(',').map(s => s.trim()));

  return cart.filter(item => {
    const itemCodes = extractSkuCodes(item.id, isAdvance);
    return itemCodes.some(code => skuCodes.has(code));
  }).length;
};

/**
 * - limits: Array of voucher limit configurations from API.
 * - voucherId: The voucher identifier to match against limits.
 * - cart: Current cart items (default empty array).
 * - fallbacks: Object containing fallback values for stock/remaining if no limit is matched.
 * - isAdvance: Boolean flag to indicate advance booking logic.
 */

export const getVoucherLimitInfo = (
  limits,
  voucherId,
  cart = [],
  fallbacks = {},
  isAdvance = false,
) => {
  const matchedLimit = matchVoucherLimit(limits, voucherId, isAdvance);
  const stock = matchedLimit ? matchedLimit.limit : fallbacks.fallbackStock;
  const apiUsed = matchedLimit ? matchedLimit.used : 0;

  const cartUsed = matchedLimit
    ? getCartUsageForLimit(cart, matchedLimit, isAdvance)
    : 0;

  const used = apiUsed + cartUsed;

  const remaining = matchedLimit
    ? matchedLimit.limit - used
    : fallbacks.fallbackRemaining;

  const isFullyReserved = remaining <= 0;

  const hasLimitData = Boolean(matchedLimit);
  const isPurchased = matchedLimit ? matchedLimit.used >= matchedLimit.limit : false;

  return {
    stock,
    used,
    remaining,
    isFullyReserved,
    hasLimitData,
    isPurchased,
    matchedLimit,
  };
};

// This function checks voucher ID is in any SKU list, has club promotion tag and is purchased based on getVoucherLimitInfo
export const hasEligibleVoucher = (vendor, limits, cart, config) => {
  if (!vendor?.vouchers?.length || !limits) return false;

  return vendor?.vouchers.some(voucher => {
    const matchedLimit = matchVoucherLimit(limits, voucher?.id, voucher, false);
    if (!matchedLimit) return false;

    const matchesPromotion
      = voucher?.promotionTag?.toString() === config?.frontend?.general?.subscriptionPromotionTag?.id;
    if (!matchesPromotion) return false;

    const { isPurchased } = getVoucherLimitInfo(
      limits,
      voucher?.id,
      cart,
      {
        fallbackStock: voucher.stock?.count,
        fallbackRemaining: voucher.stock?.remaining,
      },
      false,
    );

    return isPurchased;
  });
};

// Check whether Nando's vendor exhausted voucher limit
export const isNandosVoucherLimitReached = (vendor, limits, cart) => {
  if (!vendor?.vouchers?.length || !limits) return false;

  return vendor?.vouchers.some(voucher => {
    const matchedLimit = matchVoucherLimit(limits, voucher?.id, voucher, false);
    if (!matchedLimit) return false;

    const { isPurchased } = getVoucherLimitInfo(
      limits,
      voucher?.id,
      cart,
      {
        fallbackStock: voucher?.stock?.count,
        fallbackRemaining: voucher?.stock?.remaining,
      },
      false,
    );

    return isPurchased;
  });
};
